import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useAtomValue } from "jotai";

import useRecord from "../../../api/api-hooks/records/useRecord";
import { View } from "../../../types/PageInterfaces";
import { Page } from "../../../types/GlobalInterfaces";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import TenantAPI from "../../../api/api/TenantAPI";
import StrUtils from "../../../utils/StrUtils";
import { callFunctionIfExists } from "../../../utils/GeneralUtils";
import DocumentToolbar from "./DocumentToolbar";
import RichTextEditorV2 from "../../CRM/RichTextEditorV2";

interface Props {
  view: View;
  document: any;
  page: Page;
  useToolbar?: boolean;
  handleBack?: () => void;
  toolbarCSS?: string;
}

/*
  document: {
    ModuleAPIName: "",
    id: ""
  }
*/
const PageViewDocument = ({
  view,
  document,
  page,
  useToolbar = true,
  handleBack = undefined,
  toolbarCSS,
}: Props) => {
  const { workspaceID } = useParams();
  const [writeMode, setWriteMode] = useState(false);
  const { record, recordLoading } = useRecord({
    moduleAPIName: document?.ModuleAPIName,
    recordID: document?.id,
  });
  const [richText, setRichText] = useState(record?.Body?.RichText);
  const queryClient = useQueryClient();
  const tenantID = useAtomValue(tenantIDAtom);

  useEffect(() => {
    if (!record) return;

    const richText = record?.Body?.RichText || "";

    setRichText(richText);
  }, [record]);

  const handleCancel = () => {
    setRichText(record?.Body?.RichText || "");
    setWriteMode(false);
  };

  const handleNewRecord = (newRecord: any) => {
    queryClient.setQueryData(
      [tenantID, "record", document.ModuleAPIName, document.id],
      newRecord
    );

    queryClient.invalidateQueries({
      queryKey: [tenantID, "records", "Documents"],
    });
  };

  const handleSave = () => {
    setWriteMode(false);

    TenantAPI.modifyRecord("Documents", record.id, {
      payload: {
        Body: {
          RichText: richText,
          Plaintext: StrUtils.convertMarkupToPlaintext(richText),
        },
      },
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: {
          id: StrUtils.uuidv4(),
          Type: "edited-document-view",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Icon,
          },
          View: {
            id: view.id,
            Name: view.Name,
            Type: view.Type,
          },
          DocumentName: view.Name,
        },
      },
    }).then((data) => handleNewRecord(data.data.data.data));
  };

  const handleTitleSave = (newTitle: string) => {
    TenantAPI.modifyRecord("Documents", record.id, {
      payload: { Name: newTitle },
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: {
          id: StrUtils.uuidv4(),
          Type: "edited-document-view",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Icon,
          },
          View: {
            id: view.id,
            Name: view.Name,
            Type: view.Type,
          },
          DocumentName: view.Name,
        },
      },
    }).then((data) => handleNewRecord(data.data.data.data));
  };

  const handleSettingsSave = (type: string, audience: string, onSave: any) => {
    TenantAPI.modifyRecord("Documents", record.id, {
      payload: { Type: type, Audience: audience },
      _UserActivity: {
        WorkspaceID: workspaceID,
        Activity: {
          id: StrUtils.uuidv4(),
          Type: "edited-document-view",
          Page: {
            id: page.id,
            Name: page.Name,
            Logo: page.Icon,
          },
          View: {
            id: view.id,
            Name: view.Name,
            Type: view.Type,
          },
          DocumentName: view.Name,
        },
      },
    }).then((data) => {
      handleNewRecord(data.data.data.data);
      callFunctionIfExists(onSave);
    });
  };

  return (
    <>
      {useToolbar && (
        <DocumentToolbar
          document={record}
          loading={recordLoading}
          writeMode={writeMode}
          onEdit={() => setWriteMode(true)}
          onCancel={handleCancel}
          onSave={handleSave}
          onTitleSave={handleTitleSave}
          onSettingsSave={handleSettingsSave}
          handleBack={handleBack}
          className={toolbarCSS}
        />
      )}
      <div className="mt-3">
        <RichTextEditorV2
          presentAsDocument={true}
          isEditing={writeMode}
          value={richText || record?.Body?.Richtext}
          onChange={(val) => setRichText(val)}
          loading={recordLoading}
          toggleEdit={() => setWriteMode(true)}
          useBrie={false}
        />
      </div>
    </>
  );
};

export default PageViewDocument;
